/**
 * Why a checkout-rule request failed, from the backend's closed list
 * (COM-1069). Shipped alongside `extensions.extras` on every rule-related
 * error — the request's own `code` says `BAD_REQUEST`, `extras` names the
 * rules involved, and this says *why* so cinnamon can decide what to do.
 *
 * Yao's `getCheckoutRule` guide §7 and the backend parent's §10. The list is
 * **closed**: the backend ships reasons as needed and never renames or
 * removes one, so a value cinnamon has not heard of is one older cinnamon
 * does not know yet. `isCheckoutRuleErrorReason` narrows against this list,
 * and `getFailedCheckoutRules` returns `undefined` for anything the type
 * guard refuses — so an unknown reason in cinnamon reads as "no reason" at
 * `useCheckoutRuleRecovery` and takes the **default redirect** branch
 * (which is what a pre-COM-1069 storefront reaches too). Review of #812
 * (2026-10-06) corrected the earlier "falls through to the generic branch"
 * claim here — the generic arm requires `reason !== undefined`, so an
 * unrecognised reason routes the shopper to the picker rather than
 * surfacing a toast and a Datadog line that would help an operator diagnose
 * it. Treat that as a conservative default rather than ideal observability;
 * widening it to the generic arm is a one-line change (strip the type
 * guard from `getFailedCheckoutRules` and switch on raw strings), left for
 * a follow-up once a new reason actually ships.
 *
 * Reasons the recovery hook branches on today:
 *
 * - `required_selection_missing` — `createOrder` blocked because a required
 *   rule has no pick. The redirect to the picker.
 * - `selection_not_offered` — the stored pick is no longer in the options
 *   list. Clear the local selection and the session snapshot's pick
 *   (COM-1765), raise a toast, **then fall through to
 *   the picker route** so `useCheckoutRuleOptions` refetches on mount; the
 *   clear alone does not refetch anything, since `clearSelection` leaves
 *   `available` untouched (review of #812).
 * - `addon_failed` / `addon_invalid_response` — the storefront's ERP add-on
 *   is down or returned garbage. Toast + Datadog; the retry affordance
 *   belongs to the storefront, not to cinnamon.
 *
 * The others (`selection_malformed`, `no_source`, `unknown_rule_type`,
 * `session_inactive`, `override_invalid`) all fall through to the generic
 * error toast — they describe cinnamon-side authoring bugs or wire-shape
 * drift that a shopper cannot resolve themselves.
 */
export const CHECKOUT_RULE_ERROR_REASONS = [
  'required_selection_missing',
  'selection_not_offered',
  'selection_malformed',
  'no_source',
  'unknown_rule_type',
  'addon_failed',
  'addon_invalid_response',
  'session_inactive',
  'override_invalid',
] as const

export type CheckoutRuleErrorReason = (typeof CHECKOUT_RULE_ERROR_REASONS)[number]

/**
 * `true` for a value in the closed list, `false` for anything else —
 * including `undefined`, which is what a storefront that has not shipped
 * COM-1069 returns.
 *
 * ⚠️ **Deliberately strict.** Falling through on an unknown reason is what
 * keeps a cinnamon release safe against a backend that ships a new reason
 * cinnamon does not know yet: the recovery path lands on the default
 * redirect branch, same as a pre-COM-1069 storefront reaches with no
 * `reason` at all. Review of #812 (2026-10-06) corrected an earlier
 * reference to a non-existent `isHex` guard here.
 */
export const isCheckoutRuleErrorReason = (
  value: unknown,
): value is CheckoutRuleErrorReason =>
  typeof value === 'string' &&
  (CHECKOUT_RULE_ERROR_REASONS as readonly string[]).includes(value)
